import { useEffect, useRef, useState } from 'react';
import { Box, keyframes } from '@mui/material';

import { useLocale } from '../LocaleProvider';
import { getAppThemeColor } from '../../helpers/themeHelper';
import HeaderMenu from './HeaderMenu';
import NuiTypography from '../NuiTypography';
import NuiIconButton from '../NuiIconButton';
import NuiCircularProgress from '../NuiCircularProgress';
import { globals } from '../../theme';

const animLogo = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-1rem);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const animProfile = keyframes`
  0% {
    opacity: 0;
    transform: translateX(1rem);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const animIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

interface HeaderProps {
  beta: boolean;
  embed: boolean;
  state: string;
  auth: AuthTypes;
  profile: ProfileTypes;
  onAction: Function;
}

const Header: React.FC<HeaderProps> = ({
  beta,
  embed,
  state,
  auth,
  profile,
  onAction,
}) => {
  const profileRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState(false);
  const [hover, setHover] = useState(false);
  const [color, setColor] = useState(getAppThemeColor());

  const text = useLocale().text;

  const isBack =
    state !== 'generating' && state !== 'searching' && state !== 'saving';
  const isActive = isBack && hover;

  useEffect(() => {
    if (!hover) return;
    setColor(getAppThemeColor());
  }, [hover]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        width: '100%',
        height: 80,
        userSelect: 'none',
      }}>
      {/* Overlay */}
      <Box
        sx={(theme) => ({
          position: 'absolute',
          width: '100%',
          height: 72,
          background: embed
            ? `linear-gradient(${globals.colors.bgBase}, rgba(24, 29, 56, 0))`
            : `linear-gradient(${globals.colors.black}, ${globals.colors.black0})`,
          [theme.breakpoints.up('lg')]: {
            display: 'none',
          },
        })}
      />

      {/* Embedded version gets minimal header... */}
      {embed && (
        <Box
          sx={(theme) => ({
            position: 'relative',
            padding: `0 ${theme.spacing(4)}`,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}>
          {/* Title */}
          <NuiTypography variant="title-bold" textAlign="center" width="100%">
            {text.HEADER.TITLE}
          </NuiTypography>
          {/* Close */}
          <NuiIconButton
            icon="cross-small"
            sx={(theme) => ({
              position: 'absolute',
              right: theme.spacing(2),
              '&.MuiIconButton-root.MuiIconButton-sizeMedium svg': {
                width: 24,
                height: 24,
                fill: globals.colors.white80,
              },
            })}
            onClick={() => onAction('quit')}
          />
        </Box>
      )}

      {/* ...Otherwise full logo, profile and nav */}
      {!embed && (
        <Box
          sx={(theme) => ({
            position: 'relative',
            padding: `0 ${theme.spacing(4)}`,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}>
          {/* Logo */}
          <Box
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => isBack && onAction('back')}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              cursor: isActive ? 'pointer' : null,
              filter: `drop-shadow(0 2px 4px ${globals.colors.black40})`,
              opacity: 0,
              animation: `${animLogo} 0.3s 0.3s forwards`,
              svg: {
                height: 26,
                fill: isActive ? color : globals.colors.white,
                transition: 'fill 0.3s',
              },
              [theme.breakpoints.up('lg')]: {
                height: 32,
              },
            })}>
            <svg
              width="155"
              height="26"
              viewBox="0 0 155 26"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                height: '100%',
                width: 'auto',
              }}>
              <path d="M64.8535 11.0807C63.4093 11.0807 62.1386 11.7614 61.6182 12.8128L61.4837 13.0801L61.2784 11.2981H58.5918V26.0178H61.4519V19.4812L61.6182 19.8162C62.1386 20.8677 63.4093 21.5484 64.8535 21.5484C67.6356 21.5484 69.7311 19.2995 69.7311 16.3163C69.7311 13.3332 67.6356 11.0807 64.8535 11.0807ZM64.3119 18.8326C63.6463 18.8326 63.008 18.5663 62.5373 18.0924C62.0667 17.6185 61.8023 16.9758 61.8023 16.3056C61.8023 15.6354 62.0667 14.9927 62.5373 14.5188C63.008 14.0449 63.6463 13.7787 64.3119 13.7787C64.9775 13.7787 65.6158 14.0449 66.0865 14.5188C66.5571 14.9927 66.8215 15.6354 66.8215 16.3056C66.8215 16.9758 66.5571 17.6185 66.0865 18.0924C65.6158 18.5663 64.9775 18.8326 64.3119 18.8326Z" />
              <path d="M74.9627 21.5484C77.0865 21.5484 79.2386 20.6182 79.2386 18.5403C79.2386 17.079 78.251 16.0454 76.2228 15.4075C75.8051 15.2685 75.4441 15.1544 75.1397 15.0511C73.8088 14.6234 73.4477 14.5093 73.4477 14.103C73.4477 13.6967 74.0282 13.4258 74.6016 13.4258C75.0583 13.4258 75.8264 13.6005 75.9078 14.2349H78.7926C78.697 12.3709 76.9484 11.0735 74.5025 11.0735C71.9469 11.0735 70.57 12.6346 70.57 14.103C70.57 15.5714 71.554 16.5658 73.6601 17.1539L74.322 17.3356C75.8618 17.7633 76.3786 17.9059 76.3786 18.4726C76.3786 19.0393 75.6494 19.1854 75.0229 19.1854C73.9822 19.1854 73.253 18.8076 73.1398 18.2124H70.2727C70.3718 20.3402 72.0779 21.5484 74.9627 21.5484Z" />
              <path d="M102.983 13.7787C103.297 13.7857 103.607 13.8534 103.896 13.9783L104.275 11.4513C103.962 11.2108 103.577 11.085 103.184 11.0949C102.476 11.0949 101.786 11.4799 101.117 12.239L100.993 12.3816L100.834 11.3123H98.1474V21.3452H101.004V17.3071C101.004 15.0297 101.705 13.7787 102.983 13.7787Z" />
              <path d="M86.6931 11.2838H84.3038V8.24723H81.4438V11.2838H79.7554V13.9818H81.4438V21.3452H84.3038V13.9818H86.6931V11.2838Z" />
              <path d="M97.0041 16.4553C97.0041 13.3403 94.9051 11.0807 92.0096 11.0807C89.1142 11.0807 87.0117 13.2583 87.0117 16.2522C87.0117 19.3208 89.1885 21.5484 92.1902 21.5484C94.9157 21.5484 96.5085 19.6416 96.7917 18.2338L94.3139 17.9665C94.0712 18.3018 93.751 18.5726 93.3809 18.7551C93.0108 18.9377 92.6021 19.0267 92.1902 19.0143C90.5619 19.0143 89.9177 18.1019 89.854 17.2537V17.1574H96.9935L97.0041 16.4553ZM89.8646 15.2507V15.1544C89.9319 14.2634 90.8309 13.6147 91.9884 13.6147C93.3122 13.6147 94.0626 14.3881 94.1122 15.1544V15.2507H89.8646Z" />
              <path d="M54.3195 11.2838L54.1142 13.0659L53.9832 12.7986C53.4593 11.7293 52.2134 11.0664 50.7657 11.0664C47.9729 11.0664 45.8703 13.3154 45.8703 16.3021C45.8703 19.2888 47.9729 21.5341 50.7657 21.5341C52.1815 21.5341 53.4593 20.8534 53.9832 19.802L54.1142 19.5347L54.3195 21.3167H57.0096V11.2838H54.3195ZM51.286 18.8326C50.7895 18.8311 50.3046 18.6815 49.8926 18.4025C49.4806 18.1235 49.16 17.7277 48.9715 17.2652C48.7829 16.8027 48.7349 16.2943 48.8334 15.8043C48.9319 15.3143 49.1725 14.8648 49.5248 14.5125C49.8771 14.1603 50.3253 13.9212 50.8126 13.8254C51.2999 13.7297 51.8045 13.7817 52.2625 13.9748C52.7204 14.1679 53.1112 14.4935 53.3854 14.9103C53.6596 15.3271 53.8048 15.8164 53.8027 16.3163C53.81 16.6503 53.7498 16.9823 53.6257 17.2921C53.5016 17.6019 53.3162 17.883 53.0808 18.1184C52.8454 18.3538 52.5648 18.5385 52.2563 18.6613C51.9477 18.784 51.6176 18.8423 51.286 18.8326Z" />
              <path d="M106.051 19.8341C106.055 19.7538 106.041 19.6738 106.011 19.5992C105.981 19.5247 105.936 19.4574 105.879 19.4018C105.821 19.3462 105.752 19.3035 105.677 19.2767C105.602 19.2498 105.522 19.2394 105.443 19.246H104.841V20.9603H105.17V20.4221H105.365L105.676 20.9389H106.066L105.694 20.3616C105.801 20.3222 105.893 20.2501 105.957 20.1556C106.021 20.061 106.054 19.9486 106.051 19.8341ZM105.443 20.0942H105.17V19.5703H105.443C105.479 19.5649 105.517 19.5678 105.552 19.5789C105.587 19.5899 105.62 19.6087 105.647 19.634C105.674 19.6593 105.695 19.6904 105.709 19.725C105.722 19.7596 105.728 19.7969 105.726 19.8341C105.726 19.8706 105.719 19.9069 105.705 19.9405C105.691 19.974 105.67 20.0041 105.643 20.0288C105.616 20.0534 105.584 20.072 105.55 20.0833C105.515 20.0946 105.479 20.0983 105.443 20.0942Z" />
              <path d="M105.4 18.5118C105.084 18.5118 104.774 18.6063 104.511 18.7833C104.248 18.9603 104.043 19.2119 103.922 19.5063C103.801 19.8006 103.769 20.1245 103.831 20.437C103.893 20.7495 104.045 21.0366 104.269 21.2619C104.493 21.4872 104.778 21.6406 105.088 21.7028C105.398 21.7649 105.72 21.733 106.012 21.6111C106.305 21.4892 106.555 21.2827 106.73 21.0178C106.906 20.7528 107 20.4414 107 20.1228C107 19.6955 106.831 19.2857 106.531 18.9836C106.231 18.6815 105.824 18.5118 105.4 18.5118ZM105.4 21.4023C105.148 21.403 104.901 21.3282 104.691 21.1875C104.481 21.0468 104.317 20.8464 104.22 20.6119C104.123 20.3773 104.098 20.119 104.147 19.8698C104.196 19.6205 104.317 19.3916 104.496 19.2119C104.674 19.0323 104.901 18.91 105.149 18.8606C105.396 18.8112 105.653 18.8369 105.886 18.9345C106.119 19.032 106.318 19.197 106.458 19.4086C106.597 19.6201 106.672 19.8687 106.671 20.1228C106.67 20.4618 106.536 20.7867 106.298 21.0265C106.06 21.2662 105.737 21.4013 105.4 21.4023Z" />
              <path d="M44.6881 21.3452V7.15305H41.8281V16.5907L35.0815 7.15305H32.1825V21.3452H35.039V11.9111L41.7856 21.3452H44.6881Z" />
              <path d="M23.1812 4.35881C22.9327 4.35859 22.6844 4.37407 22.4379 4.40514H22.3883L22.3565 4.36594C21.325 3.00701 19.9963 1.9058 18.4735 1.14785C16.9508 0.389895 15.275 -0.00439453 13.5764 -0.00439453C11.8777 -0.00439453 10.2019 0.389895 8.6792 1.14785C7.15646 1.9058 5.82774 3.00701 4.79627 4.36594L4.76795 4.40514H4.7184C4.47063 4.37417 4.2212 4.35869 3.97153 4.35881H2.4778V7.14949L2.42824 7.17444C1.7075 7.51544 1.09806 8.05599 0.671166 8.7329C0.244275 9.4098 0.0175781 10.1951 0.0175781 10.9969C0.0175781 11.7988 0.244275 12.584 0.671166 13.2609C1.09806 13.9378 1.7075 14.4784 2.42824 14.8194L2.4778 14.8408V14.8978C2.4778 17.8659 3.64879 20.7124 5.73316 22.8112C7.81753 24.91 10.6445 26.089 13.5923 26.089C16.54 26.089 19.3671 24.91 21.4514 22.8112C23.5358 20.7124 24.7068 17.8659 24.7068 14.8978V14.8408L24.7563 14.8194C25.4771 14.4784 26.0865 13.9378 26.5134 13.2609C26.9403 12.584 27.167 11.7988 27.167 10.9969C27.167 10.1951 26.9403 9.4098 26.5134 8.7329C26.0865 8.05599 25.4771 7.51544 24.7563 7.17444L24.7068 7.14949V4.35881H23.1812ZM7.70586 5.27121C8.47435 4.48996 9.38898 3.86979 10.3969 3.44655C11.4048 3.02332 12.4861 2.8054 13.5781 2.8054C14.6702 2.8054 15.7514 3.02332 16.7594 3.44655C17.7673 3.86979 18.6819 4.48996 19.4504 5.27121L19.5318 5.35675L19.4292 5.41378C19.0066 5.6473 18.6009 5.91051 18.2151 6.20144L18.1691 6.23352L18.123 6.20857C16.7153 5.48204 15.1599 5.09293 13.5781 5.07163C11.9964 5.09293 10.441 5.48204 9.03323 6.20857L8.98367 6.23352L8.94119 6.20144C8.55415 5.91047 8.14727 5.64727 7.72356 5.41378L7.62091 5.34606L7.70586 5.27121ZM21.6698 14.8051C21.6698 16.966 20.8173 19.0383 19.2998 20.5663C17.7823 22.0942 15.7242 22.9526 13.5781 22.9526C11.4321 22.9526 9.37396 22.0942 7.85648 20.5663C6.33901 19.0383 5.4865 16.966 5.4865 14.8051V7.72331L5.59623 7.75182C7.01209 8.10823 8.35361 9.67286 8.6651 10.0542C9.3155 9.41949 10.0849 8.92148 10.9284 8.58921C11.7719 8.25695 12.6726 8.09708 13.5781 8.11892C14.4836 8.09708 15.3844 8.25695 16.2279 8.58921C17.0714 8.92148 17.8408 9.41949 18.4912 10.0542C18.7991 9.67286 20.13 8.10823 21.56 7.75182L21.6698 7.72331V14.8051Z" />
              <g fillOpacity="0.6">
                <path d="M145.663 18.0399H148.683C148.743 18.6199 149.443 18.9999 150.483 18.9999C151.043 18.9999 151.743 18.8399 151.743 18.3799C151.743 17.8199 151.103 17.7199 149.103 17.1599C147.223 16.6399 145.963 15.7399 145.963 14.0599C145.963 12.1399 147.883 10.9799 149.963 10.9799C152.703 10.9799 154.283 12.4999 154.323 14.2799H151.283C151.263 13.6599 150.523 13.4799 150.063 13.4799C149.583 13.4799 149.003 13.6599 149.003 14.0599C149.003 14.4799 149.503 14.5399 151.703 15.2599C154.223 16.0599 154.763 17.3199 154.763 18.4399C154.763 20.4599 152.763 21.4999 150.423 21.4999C147.663 21.4999 145.723 20.3999 145.663 18.0399Z" />
                <path d="M144.904 16.2398C144.904 19.1398 142.844 21.4998 139.944 21.4998C138.324 21.4998 137.124 20.6998 136.664 19.7398L136.484 21.2998H133.644V6.2998H136.664V12.7398C137.124 11.7798 138.324 10.9798 139.944 10.9798C142.844 10.9798 144.904 13.3398 144.904 16.2398ZM141.864 16.2398C141.864 14.9398 140.884 13.8198 139.424 13.8198C137.944 13.8198 137.004 14.9398 137.004 16.2398C137.004 17.5398 137.944 18.6398 139.424 18.6398C140.884 18.6398 141.864 17.5398 141.864 16.2398Z" />
                <path d="M120.585 16.2399C120.585 13.3399 122.645 10.9799 125.545 10.9799C127.145 10.9799 128.345 11.7799 128.825 12.7399L129.005 11.1799H131.845V21.2999H129.005L128.825 19.7399C128.345 20.6999 127.145 21.4999 125.545 21.4999C122.645 21.4999 120.585 19.1399 120.585 16.2399ZM123.605 16.2399C123.605 17.5399 124.585 18.6399 126.065 18.6399C127.525 18.6399 128.465 17.5399 128.465 16.2399C128.465 14.9399 127.525 13.8199 126.065 13.8199C124.585 13.8199 123.605 14.9399 123.605 16.2399Z" />
                <path d="M113.82 18.4399H119.62V21.2999H110.8V7.09985H113.82V18.4399Z" />
              </g>
            </svg>
            {beta && (
              <Box
                sx={(theme) => ({
                  position: 'relative',
                  margin: `${theme.spacing(1)} 0 0 ${theme.spacing(2)}`,
                  height: 20,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: `0 ${theme.spacing(1)}`,
                  pointerEvents: 'none',
                  borderRadius: 0.5,
                  backgroundColor: isActive ? color : globals.colors.white,
                  transition: 'background-color 0.3s',
                })}>
                <NuiTypography
                  variant="caption-small-bold"
                  textAlign="center"
                  color={globals.colors.black}
                  sx={{
                    lineHeight: 0,
                    textTransform: 'uppercase',
                  }}>
                  {`Beta`}
                </NuiTypography>
              </Box>
            )}
          </Box>

          {/* Profile */}
          {auth && (
            <Box
              ref={profileRef}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                animation: `${animProfile} 0.3s 0.3s forwards`,
                filter: 'brightness(1)',
                transition: 'filter 0.3s',
                '&:hover': {
                  cursor: 'pointer',
                  filter: 'brightness(0.6)',
                  transition: 'filter 0.3s',
                },
              }}
              onClick={() => setMenu(true)}>
              {(profile.name || profile.email) && (
                <NuiTypography
                  variant="body-demi"
                  textAlign="center"
                  marginRight={3}
                  color={globals.colors.white60}
                  sx={(theme) => ({
                    display: 'none',
                    opacity: 0,
                    animation: `${animIn} 0.3s forwards`,
                    [theme.breakpoints.up('md')]: {
                      display: 'inline-block',
                    },
                  })}>
                  {profile.name || profile.email}
                </NuiTypography>
              )}
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 999,
                  outline: `2px solid ${globals.colors.white}`,
                  boxSizing: 'border-box',
                  boxShadow: `0px 2px 16px ${globals.colors.black40}`,
                  overflow: 'hidden',
                }}>
                {!profile.img && <NuiCircularProgress size={24} />}
                {profile.img && (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1/1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      animation: `${animIn} 0.3s 0.3s forwards`,
                    }}>
                    <img
                      src={profile.img}
                      alt={profile.name || profile.email}
                      style={{
                        width: '100%',
                        height: 'auto',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Menu */}
          <HeaderMenu
            target={profileRef}
            open={menu}
            onAction={onAction}
            onClose={() => setMenu(false)}
          />
        </Box>
      )}
    </Box>
  );
};

export default Header;
